/**
 * Contact Support Form - Write-once support request intake
 * 
 * CONSTRAINTS (audit-safe):
 * - Single addDoc() write, fail-loud on error
 * - No retries, no silent fallback
 * - Isolated collection (supportRequests/)
 * - Explicit try/catch with user-visible errors
 * - Success confirmation only after write success
 */

import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/auth/AuthProvider';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';
import { logEvent } from '@/lib/analytics';

const REQUEST_TYPES = [
  { value: 'bug', label: 'Report a Bug' },
  { value: 'feature-request', label: 'Request a Feature' },
  { value: 'account-issue', label: 'Account Issue' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'general-help', label: 'General Help' },
  { value: 'other', label: 'Other' },
] as const;

const MAX_NOTE_LENGTH = 1000;

type RequestType = typeof REQUEST_TYPES[number]['value'];

interface ContactSupportFormProps {
  userRole: 'SRL' | 'PRO';
  caseId?: string; // Optional case context
}

export function ContactSupportForm({ userRole, caseId }: ContactSupportFormProps) {
  const { user } = useAuth();
  const [requestType, setRequestType] = useState<RequestType>('general-help');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError('You must be signed in to submit a support request.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Single write operation - fail-loud
      // P2 spec: userId, role, caseId?, category, message, createdAt, status
      const supportRequestData = {
        userId: user.uid,
        role: userRole,
        caseId: caseId || null,
        category: requestType,
        message: note.trim() || '',
        createdAt: serverTimestamp(),
        status: 'open' as const,
        // Legacy fields for backward compatibility
        requestType,
        requesterUid: user.uid,
        requesterEmail: user.email || 'no-email@threadlock.ai',
        requesterRole: userRole,
        optionalNote: note.trim() || null,
      };

      // Write to both supportRequests (current) and supportTickets (P2 canonical)
      await Promise.all([
        addDoc(collection(db, 'supportRequests'), supportRequestData),
        addDoc(collection(db, 'supportTickets'), supportRequestData),
      ]);

      // P2 Observability: support_ticket.created event
      try {
        await logEvent('support_ticket.created', {
          category: requestType,
          role: userRole,
          userId: user.uid,
          hasCaseContext: !!caseId,
        });
        // Legacy analytics event for backward compatibility
        await logEvent('support_request_created', {
          requestType,
          requesterRole: userRole,
        });
      } catch (analyticsError) {
        // Non-blocking analytics failure
        console.warn('[ContactSupportForm] Analytics event failed:', analyticsError);
      }

      // Success state only after write success
      setSuccess(true);
      setRequestType('general-help');
      setNote('');

      // Auto-reset success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      // Fail-loud - show explicit error to user
      console.error('[ContactSupportForm] Support request write failed:', err);
      setError(
        err instanceof Error 
          ? `Failed to submit support request: ${err.message}` 
          : 'Failed to submit support request. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-surface-panel border border-surface-default rounded-lg p-6">
      <h3 className="text-lg font-semibold text-surface-primary mb-4 flex items-center gap-2">
        <Send className="w-5 h-5 text-brand-orange" />
        Contact Support
      </h3>

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-900">Request Submitted</p>
            <p className="text-sm text-green-700 mt-1">
              Your support request has been received. Our team will review it and respond via email.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-900">Submission Failed</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="requestType" className="block text-sm font-medium text-surface-primary mb-2">
            What can we help you with?
          </label>
          <select
            id="requestType"
            value={requestType}
            onChange={(e) => setRequestType(e.target.value as RequestType)}
            className="w-full px-3 py-2 bg-surface-panel border border-surface-default rounded-md text-surface-primary focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent"
            disabled={submitting}
          >
            {REQUEST_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="note" className="block text-sm font-medium text-surface-primary mb-2">
            Additional Details (optional)
          </label>
          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MAX_NOTE_LENGTH))}
            rows={4}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="Please provide any relevant details that will help us assist you..."
            className="w-full px-3 py-2 bg-surface-panel border border-surface-default rounded-md text-surface-primary placeholder-surface-muted focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent resize-none"
            disabled={submitting}
          />
          <p className="text-xs text-surface-muted mt-1">
            {note.length} / {MAX_NOTE_LENGTH} characters
          </p>
        </div>

        <button
          type="submit"
          disabled={submitting || !user}
          className="w-full px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit Request
            </>
          )}
        </button>
      </form>

      <p className="text-xs text-surface-muted mt-4">
        Support requests are reviewed by our team. You'll receive a response via email within 1-2 business days.
      </p>
    </div>
  );
}
